import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment/moment';

import { TimerAction } from '@core/services/news.service';

/**
 * Данные диалога акций: слайды из GET adds/timer.
 */
export interface PromoTimerDialogData {
  slides: TimerAction[];
}

/** Порог свайпа для смены слайда — доля ширины вьюпорта. */
const SWIPE_THRESHOLD_RATIO = 0.25;

/** Смещение в px, начиная с которого драг подавляет click по ссылкам. */
const DRAG_CLICK_SUPPRESS_PX = 5;

/**
 * Модальное окно акций: плавная карусель таймеров.
 *
 * Карусель: горизонтальный track, смена слайда через `translateX` с
 * CSS-переходом; стрелки-шевроны по краям (скрываются на первом/последнем
 * слайде), точки-индикаторы снизу, свайп нативными pointer-событиями,
 * клавиатура ArrowLeft/ArrowRight. Анимация отключается при
 * `prefers-reduced-motion: reduce` (см. scss).
 *
 * Каждый слайд: цветной баннер (дефолтный фон темы, поверх — опциональный
 * `backgroundCssClass` из админки), title/text, обратный отсчёт до
 * `activeUntil` (только у текущего слайда при `showTimer`), белый футер с
 * кнопками из `links[]`.
 */
@Component({
  selector: 'ms-promo-timer-dialog',
  imports: [CommonModule, TranslatePipe],
  standalone: true,
  templateUrl: './promo-timer-dialog.component.html',
  styleUrl: './promo-timer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoTimerDialogComponent implements OnDestroy {
  /** Индекс текущего слайда. */
  public readonly currentIndex = signal(0);

  /** Остаток времени до конца акции текущего слайда. */
  public readonly timer = signal({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  /** Живое смещение track при перетаскивании, px. */
  public readonly dragOffset = signal(0);

  /** Идёт перетаскивание (CSS-переход track выключен). */
  public readonly isDragging = signal(false);

  /** Текущий слайд (нужен отсчёту и id обёртки); undefined при пустом массиве. */
  public readonly slide = computed<TimerAction | undefined>(
    () => this.data.slides[this.currentIndex()],
  );

  /** Трансформ track: слайд + живое смещение драга. */
  public readonly trackTransform = computed(
    () => `translateX(calc(${-this.currentIndex() * 100}% + ${this.dragOffset()}px))`,
  );

  private _timerId?: ReturnType<typeof setInterval>;

  /** Id таймаута сброса _suppressClick — чистится в ngOnDestroy. */
  private _suppressResetId?: ReturnType<typeof setTimeout>;

  /** clientX начала драга (null — драга нет). */
  private _dragStartX: number | null = null;

  /** pointerId активного драга — чужие указатели (multi-touch) игнорируются. */
  private _dragPointerId: number | null = null;

  /** Драг превысил порог шума — ближайший click по ссылке подавить. */
  private _suppressClick = false;

  /**
   * Capture-слушатель click на хосте: Angular НЕ поддерживает capture-
   * модификаторы событий в шаблоне ((click.capture) — мёртвый код),
   * поэтому нативный addEventListener с capture=true.
   */
  private readonly _handleClickCapture = (event: MouseEvent): void => {
    if (this._suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
    }
  };

  constructor(
    public readonly dialogRef: MatDialogRef<PromoTimerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: PromoTimerDialogData,
    private readonly _elementRef: ElementRef<HTMLElement>,
  ) {
    this._elementRef.nativeElement.addEventListener('click', this._handleClickCapture, true);

    effect(() => {
      // Перезапуск отсчёта при смене слайда
      this._restartCountdown(this.slide());
    });
  }

  /** Текущий слайд первый. */
  public isFirst(): boolean {
    return this.currentIndex() <= 0;
  }

  /** Текущий слайд последний. */
  public isLast(): boolean {
    return this.currentIndex() >= this.data.slides.length - 1;
  }

  /** Перейти к слайду по индексу (клик по точке/стрелке). */
  public goTo(index: number): void {
    const clamped = Math.min(Math.max(index, 0), this.data.slides.length - 1);
    this.currentIndex.set(clamped);
  }

  /** Следующий слайд; на последнем — закрыть диалог (автоистечение таймера). */
  public next(): void {
    if (this.isLast()) {
      this.close();
      return;
    }
    this.goTo(this.currentIndex() + 1);
  }

  /** Предыдущий слайд. */
  public prev(): void {
    this.goTo(this.currentIndex() - 1);
  }

  /** Закрыть диалог. */
  public close(): void {
    this.dialogRef.close();
  }

  /** Клавиатура: ArrowLeft/ArrowRight переключают слайды в пределах границ. */
  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' && !this.isFirst()) {
      this.prev();
      event.preventDefault();
    } else if (event.key === 'ArrowRight' && !this.isLast()) {
      this.goTo(this.currentIndex() + 1);
      event.preventDefault();
    }
  }

  /**
   * Начало возможного свайпа (мышь — только основной кнопкой).
   *
   * Режим драга НЕ включается здесь: isDragging/setPointerCapture/_suppressClick
   * срабатывают в onPointerMove при первом реальном движении. Иначе обычный
   * клик по ссылке ломается: класс .dragging даёт pointer-events: none
   * ссылкам, а pointer capture уносит click у дочерних элементов.
   */
  public onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    if (this.data.slides.length < 2 || this._dragStartX !== null) {
      return;
    }
    this._dragStartX = event.clientX;
    this._dragPointerId = event.pointerId;
    // Сброс подавителя от прошлого драга, завершившегося без click
    this._suppressClick = false;
  }

  /** Живое смещение track за указателем; драг включается после порога движения. */
  public onPointerMove(event: PointerEvent): void {
    if (this._dragStartX === null || event.pointerId !== this._dragPointerId) {
      return;
    }
    const deltaX = event.clientX - this._dragStartX;

    if (!this.isDragging()) {
      // До порога — обычный клик/выделение, никаких побочных эффектов
      if (Math.abs(deltaX) <= DRAG_CLICK_SUPPRESS_PX) {
        return;
      }
      this.isDragging.set(true);
      this._suppressClick = true;
      try {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      } catch {
        // В тестовом окружении pointerId синтетический — захват недоступен
      }
    }

    this.dragOffset.set(deltaX);
  }

  /** Конец свайпа: снап к соседнему слайду при превышении порога. */
  public onPointerUp(event: PointerEvent): void {
    if (this._dragStartX === null || event.pointerId !== this._dragPointerId) {
      return;
    }
    const deltaX = event.clientX - this._dragStartX;
    const viewportWidth = (event.currentTarget as HTMLElement).clientWidth;

    // Вьюпорт без ширины (диалог скрыт/анимируется) — свайп не обрабатываем
    if (!viewportWidth) {
      this._endDrag();
      return;
    }
    const threshold = viewportWidth * SWIPE_THRESHOLD_RATIO;

    // Свайп влево (deltaX < 0) — следующий слайд, вправо — предыдущий.
    if (deltaX <= -threshold && !this.isLast()) {
      this.goTo(this.currentIndex() + 1);
    } else if (deltaX >= threshold && !this.isFirst()) {
      this.goTo(this.currentIndex() - 1);
    }
    this._endDrag();
  }

  /** Отмена свайпа (pointercancel) — вернуть track к текущему слайду. */
  public onPointerCancel(event: PointerEvent): void {
    if (event.pointerId === this._dragPointerId) {
      this._endDrag();
    }
  }

  private _endDrag(): void {
    this._dragStartX = null;
    this._dragPointerId = null;
    this.dragOffset.set(0);
    this.isDragging.set(false);
    // click после pointerup приходит синхронно и подавляется по флагу;
    // сброс — микротаском, чтобы «оборванный» драг (без click) не залипал
    this._suppressResetId = setTimeout(() => {
      this._suppressClick = false;
      this._suppressResetId = undefined;
    });
  }

  /** Перезапустить обратный отсчёт для слайда. */
  private _restartCountdown(slide: TimerAction | undefined): void {
    this._clearTimer();

    if (!slide?.showTimer) {
      return;
    }

    this._updateTimer(slide);
    // Паттерн как в TimerNewsComponent: интервал в зоне Angular — сигналы
    // триггерят CD каждый тик, диалог открыт из зоны приложения.
    this._timerId = setInterval(() => this._updateTimer(slide), 1000);
  }

  /** Обновить остаток времени; при истечении перейти дальше или закрыть. */
  private _updateTimer(slide: TimerAction): void {
    const duration = moment.duration(moment(slide.activeUntil).diff(moment()));

    if (duration.asMilliseconds() <= 0) {
      this.timer.set({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      // Итеративно пропускаем подряд просроченные слайды (без рекурсивной
      // цепочки next→effect→next); все просрочены — закрываем диалог.
      const nextLiveIndex = this._nextLiveSlideIndex(this.currentIndex() + 1);
      if (nextLiveIndex === null) {
        this.close();
      } else {
        this.goTo(nextLiveIndex);
      }
      return;
    }

    this.timer.set({
      days: Math.floor(duration.asDays()),
      hours: duration.hours(),
      minutes: duration.minutes(),
      seconds: duration.seconds(),
    });
  }

  /**
   * Индекс первого «живого» слайда, начиная с `fromIndex`
   * (не просроченного по activeUntil; showTimer=false — тоже «живой»),
   * либо null, если таких нет.
   */
  private _nextLiveSlideIndex(fromIndex: number): number | null {
    for (let i = fromIndex; i < this.data.slides.length; i++) {
      const slide = this.data.slides[i];
      if (!slide.showTimer || moment(slide.activeUntil).isAfter(moment())) {
        return i;
      }
    }
    return null;
  }

  private _clearTimer(): void {
    if (this._timerId) {
      clearInterval(this._timerId);
      this._timerId = undefined;
    }
  }

  ngOnDestroy(): void {
    this._elementRef.nativeElement.removeEventListener('click', this._handleClickCapture, true);
    if (this._suppressResetId) {
      clearTimeout(this._suppressResetId);
    }
    this._clearTimer();
  }
}
